import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Plot from "react-plotly.js";

function InputsGraph({ lap, timeRange, lapId, merged, mergedInputType, selectedLaps, inputsXAxisMode }) {
  // Defensive: handle missing or malformed data
  if (!lap) return <div style={{color: 'red'}}>No lap data available.</div>;

  // F1-style colors
  const colors = merged ? {
    throttle: '#00AEEF', // Blue
    brake: '#FFB800',    // Yellow
    speed: '#FFFFFF',    // White
    background: '#1E1E1E',
    grid: '#333333',
    text: '#FFFFFF',
    lines: [
      '#00AEEF',  // Blue
      '#FFB800',  // Yellow
      '#9000FF',  // Purple
      '#FFD93D',  // Gold
      '#FF7F50',  // Orange
      '#7B68EE',  // Medium Slate Blue
      '#4ECDC4',  // Turquoise
      '#FF6B6B'   // Coral
    ]
  } : {
    throttle: '#00A86B', // Dark green (individual only)
    brake: '#FF1E1E',    // F1 red (individual only)
    speed: '#FFFFFF',    // White
    background: '#1E1E1E',
    grid: '#333333',
    text: '#FFFFFF',
    lines: [
      '#00A86B',  // Dark green
      '#FF1E1E',  // Red
      '#00D2BE',  // F1 teal
      '#FFB800',  // Yellow
      '#9000FF',  // Purple
      '#FF6B6B',  // Coral
      '#4ECDC4',  // Turquoise
      '#FFD93D'   // Gold
    ]
  };

  // Format lap time from filename (format: lap_mm-ss-ms.json)
  const formatLapTime = (lapId) => {
    if (!lapId) return '';
    const match = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
    if (match) {
      return `${match[1]}:${match[2]}.${match[3]}`;
    }
    return lapId;
  };

  // Calculate total lap time for time axis calculations
  const getTotalLapTime = (lapId) => {
    if (!lapId) return 0;
    const lapTimeMatch = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
    return lapTimeMatch 
      ? (parseInt(lapTimeMatch[1]) * 60 + parseInt(lapTimeMatch[2]) + parseInt(lapTimeMatch[3]) / 1000)
      : 0;
  };

  // Calculate time points for a given lap data
  // Memoize for performance
  // Calculate time points for a given lap data
  // For filtered segments, time is proportional to the selected distance range and always starts at 0
  const calculateTimePoints = useMemo(() => (lapData, lapId, startDist = null, endDist = null, totalDist = null) => {
    const totalLapTime = getTotalLapTime(lapId);
    let filteredTime = totalLapTime;
    if (startDist !== null && endDist !== null && totalDist) {
      filteredTime = ((endDist - startDist) / totalDist) * totalLapTime;
    }
    const timeStep = lapData.length > 1 ? filteredTime / (lapData.length - 1) : 0;
    return lapData.map((_, i) => {
      const time = i * timeStep;
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      const milliseconds = Math.floor((time % 1) * 1000);
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    });
  }, [lap, timeRange]);


  // Format time label without milliseconds
  const formatTimeLabel = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.split('.')[0]; // Remove milliseconds part
  };

  // Calculate cumulative distances for a lap
  function getCumulativeDistances(lapArr) {
    if (!Array.isArray(lapArr) || lapArr.length < 2) return [];
    let dists = [0];
    for (let i = 1; i < lapArr.length; i++) {
      const p1 = lapArr[i - 1];
      const p2 = lapArr[i];
      if (!p1 || !p2) {
        dists.push(dists[dists.length - 1]);
        continue;
      }
      const dx = (p2.position_x - p1.position_x) || 0;
      const dz = (p2.position_z - p1.position_z) || 0;
      dists.push(dists[dists.length - 1] + Math.sqrt(dx * dx + dz * dz));
    }
    return dists;
  }

  // Precompute full cumulative distances for the entire lap (for correct x-axis slicing)
  const fullCumulativeDistances = useMemo(() => getCumulativeDistances(lap), [lap]);


  // --- Merged mode: plot all laps on the same chart for the selected input type ---
  if (merged && Array.isArray(lap) && selectedLaps && mergedInputType) {
    // Defensive: filter out any undefined/null laps
    const validLaps = Array.isArray(lap) ? lap.filter(l => Array.isArray(l) && l.length > 0) : [];
    if (!validLaps.length) return null;
    // Use the first lap as the reference for range
    const refLap = validLaps[0];
    function findClosestIdx(arr, target) {
      let idx = 0;
      let minDiff = Math.abs(arr[0] - target);
      for (let i = 1; i < arr.length; i++) {
        const diff = Math.abs(arr[i] - target);
        if (diff < minDiff) {
          minDiff = diff;
          idx = i;
        }
      }
      return idx;
    }
    // Always slice by distance, like individual chart
    const refDists = getCumulativeDistances(refLap);
    const totalDist = refDists[refDists.length - 1];
    const startDist = (timeRange?.start ?? 0) / 100 * totalDist;
    const endDist = (timeRange?.end ?? 100) / 100 * totalDist;
    const filteredLaps = validLaps.map((lapArr) => {
      const dists = getCumulativeDistances(lapArr);
      const startIdx = findClosestIdx(dists, startDist);
      const endIdx = findClosestIdx(dists, endDist);
      return lapArr.slice(startIdx, Math.max(startIdx + 1, endIdx) + 1);
    });
    const mergedMaxLen = Math.max(...filteredLaps.map(l => l.length));
    const mergedLongestLapIdx = filteredLaps.reduce((maxIdx, l, idx) => l.length > filteredLaps[maxIdx].length ? idx : maxIdx, 0);
    // Generate a common x axis for all laps
    let commonX;
    if (inputsXAxisMode === 'distance') {
      const longestLap = filteredLaps[mergedLongestLapIdx];
      const fullDists = getCumulativeDistances(longestLap);
      commonX = fullDists;
    } else {
      // Time axis: use calculateTimePoints for the distance-sliced data, always starting from 0, and proportional to distance
      const longestLap = filteredLaps[mergedLongestLapIdx];
      const lapId = selectedLaps[mergedLongestLapIdx];
      const refLap = validLaps[0];
      const refDists = getCumulativeDistances(refLap);
      const totalDist = refDists[refDists.length - 1];
      const startDist = (timeRange?.start ?? 0) / 100 * totalDist;
      const endDist = (timeRange?.end ?? 100) / 100 * totalDist;
      commonX = calculateTimePoints(longestLap, lapId, startDist, endDist, totalDist);
    }
    // Prepare traces for each lap, aligning to the common x axis
    const mergedTraces = filteredLaps.map((lapData, idx) => {
      let y;
      if (mergedInputType === 'throttle') {
        y = lapData.map(p => p.throttle);
      } else if (mergedInputType === 'brake') {
        y = lapData.map(p => p.brake);
      } else if (mergedInputType === 'speed') {
        y = lapData.map(p => p.car_speed);
      } else if (mergedInputType === 'gear') {
        y = lapData.map(p => p.current_gear);
      }
      // X axis for this lap
      let x;
      if (inputsXAxisMode === 'distance') {
        const dists = getCumulativeDistances(lapData);
        x = dists;
        while (x.length < commonX.length) x.push(null);
      } else {
        x = commonX;
        while (y.length < commonX.length) y.push(null);
      }
      return {
        x,
        y,
        type: "scatter",
        mode: "lines",
        name: `Lap ${formatLapTime(selectedLaps[idx])}`,
        line: {
          color: colors.lines[idx % colors.lines.length],
          width: 2
        }
      };
    }).filter(Boolean);

    // Generate tick labels for the merged chart
    const mergedStep = Math.floor(commonX.length / 9) || 1;
    const mergedLabelIndices = Array.from({ length: 10 }, (_, i) => i * mergedStep);
    const mergedValidLabelIndices = mergedLabelIndices.filter(i => i < commonX.length);
    let mergedTickText, mergedTickVals;
    if (inputsXAxisMode === 'distance') {
      mergedTickText = mergedValidLabelIndices.map(i => `${Math.round(commonX[i])}m`);
      mergedTickVals = mergedValidLabelIndices.map(i => commonX[i]);
    } else {
      mergedTickText = mergedValidLabelIndices.map(i => commonX[i]?.split('.')[0]);
      mergedTickVals = mergedValidLabelIndices.map(i => commonX[i]);
    }
    const yTitle = mergedInputType === 'speed' ? 'Speed (km/h)' : mergedInputType === 'gear' ? 'Gear' : 'Input (%)';
    const chartTitle = `${mergedInputType.charAt(0).toUpperCase() + mergedInputType.slice(1)} Comparison`;

    return (
      <div style={{ width: '100%', marginBottom: '2rem', backgroundColor: colors.background, padding: '1rem', borderRadius: '8px' }}>
        {/* Merged mode chart for comparing multiple laps on the same input */}
        <h3 style={{ color: colors.text, marginBottom: '1rem' }}>{chartTitle}</h3>
        <Plot
          data={mergedTraces}
          layout={{
            width: null,
            height: 400,
            autosize: true,
            paper_bgcolor: colors.background,
            plot_bgcolor: colors.background,
            font: {
              color: colors.text
            },
            xaxis: {
              title: inputsXAxisMode === 'distance' ? 'Distance (m)' : 'Time (mm:ss)',
              tickangle: -45,
              showgrid: true,
              gridcolor: colors.grid,
              zerolinecolor: colors.grid,
              tickfont: { color: colors.text },
              ticktext: mergedTickText,
              tickvals: mergedTickVals
            },
            yaxis: {
              title: yTitle,
              showgrid: true,
              gridcolor: colors.grid,
              zerolinecolor: colors.grid,
              tickfont: { color: colors.text },
              // Only set range for non-gear charts
              ...(mergedInputType === 'gear' ? {} : { range: mergedInputType === 'speed' ? undefined : [-5, 105] })
            },
            title: {
              text: chartTitle,
              font: {
                color: colors.text,
                size: 20
              }
            },
            margin: { l: 50, r: 50, t: 50, b: 80 },
            legend: {
              font: { color: colors.text },
              bgcolor: colors.background
            },
            showlegend: true
          }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </div>
    );
  }

  // --- Individual mode ---
  // Use distance-based slicing for the selected lap
  const refLap = Array.isArray(lap) ? lap : null;
  const refDists = getCumulativeDistances(refLap);
  const totalDist = refDists.length > 0 ? refDists[refDists.length - 1] : 0;
  const startDist = (timeRange?.start ?? 0) / 100 * totalDist;
  const endDist = (timeRange?.end ?? 100) / 100 * totalDist;
  function findClosestIdx(dists, target) {
    let idx = 0;
    let minDiff = Math.abs(dists[0] - target);
    for (let i = 1; i < dists.length; i++) {
      const diff = Math.abs(dists[i] - target);
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    }
    return idx;
  }
  const filteredLap = useMemo(() => {
    if (!lap || !Array.isArray(lap)) return lap;
    const dists = getCumulativeDistances(lap);
    const startIdx = findClosestIdx(dists, startDist);
    // Remove endIdx, use directly in slice
    return lap.slice(startIdx, Math.max(startIdx + 1, findClosestIdx(dists, endDist)) + 1);
  }, [lap, startDist, endDist]);
  const filteredDists = useMemo(() => {
    if (!lap || !Array.isArray(lap)) return [];
    const dists = getCumulativeDistances(lap);
    const startIdx = findClosestIdx(dists, startDist);
    // Remove endIdx, use directly in slice
    return dists.slice(startIdx, Math.max(startIdx + 1, findClosestIdx(dists, endDist)) + 1);
  }, [lap, startDist, endDist]);

  // Memoize calculations for performance
  // For individual chart, also use proportional time for filtered segments
  const refLapForInd = Array.isArray(lap) ? lap : null;
  const refDistsForInd = getCumulativeDistances(refLapForInd);
  const totalDistForInd = refDistsForInd.length > 0 ? refDistsForInd[refDistsForInd.length - 1] : 0;
  const startDistForInd = (timeRange?.start ?? 0) / 100 * totalDistForInd;
  const endDistForInd = (timeRange?.end ?? 100) / 100 * totalDistForInd;
  const times = useMemo(() => calculateTimePoints(filteredLap, lapId, startDistForInd, endDistForInd, totalDistForInd), [filteredLap, lapId, startDistForInd, endDistForInd, totalDistForInd, calculateTimePoints]);
  const throttle = useMemo(() => filteredLap.map(p => p.throttle), [filteredLap]);
  const brake = useMemo(() => filteredLap.map(p => p.brake), [filteredLap]);
  const speed = useMemo(() => filteredLap.map(p => p.car_speed), [filteredLap]);
  // Replace dists with filteredDists
  const dists = filteredDists;

  // Calculate evenly spaced indices for labels
  const step = Math.floor((inputsXAxisMode === 'distance' ? dists.length : times.length) / 9);
  const labelIndices = Array.from({ length: 10 }, (_, i) => i * step);
  const validX = inputsXAxisMode === 'distance' ? dists : times;
  const validLabelIndices = labelIndices.filter(i => i < validX.length);
  let tickText, tickVals;
  if (inputsXAxisMode === 'distance') {
    tickText = validLabelIndices.map(i => `${Math.round(validX[i])}m`);
    tickVals = validLabelIndices.map(i => validX[i]);
  } else {
    tickText = validLabelIndices.map(i => formatTimeLabel(validX[i]));
    tickVals = validLabelIndices.map(i => validX[i]);
  }

  // If filteredLap is empty or has only one point, show a message instead of rendering the chart
  if (!filteredLap || filteredLap.length < 2) {
    // Only show warning if the range is not the full range
    if (timeRange && (timeRange.end - timeRange.start) < 100) {
      return <div style={{color: 'orange', background: '#222', padding: '1rem', borderRadius: 8, marginBottom: '1rem'}}>Track range too small to display a chart. Please select a larger range.</div>;
    }
    // For full range, fallback to showing the whole lap
    if (lap && Array.isArray(lap) && lap.length > 1) {
      // fallback: show full lap
      return (
        <div style={{ width: '100%', marginBottom: '2rem', backgroundColor: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={{ color: colors.text, marginBottom: '1rem' }}>Inputs - Lap {formatLapTime(lapId)}</h3>
          <Plot
            data={[
              {
                x: inputsXAxisMode === 'distance' ? fullCumulativeDistances : calculateTimePoints(lap, lapId, {start:0,end:100}),
                y: lap.map(p => p.throttle),
                type: 'scatter',
                mode: 'lines',
                name: 'Throttle',
                line: { color: colors.throttle, width: 2 }
              },
              {
                x: inputsXAxisMode === 'distance' ? fullCumulativeDistances : calculateTimePoints(lap, lapId, {start:0,end:100}),
                y: lap.map(p => p.brake),
                type: 'scatter',
                mode: 'lines',
                name: 'Brake',
                line: { color: colors.brake, width: 2 }
              },
              {
                x: inputsXAxisMode === 'distance' ? fullCumulativeDistances : calculateTimePoints(lap, lapId, {start:0,end:100}),
                y: lap.map(p => p.car_speed),
                type: 'scatter',
                mode: 'lines',
                name: 'Speed',
                line: { color: colors.speed, width: 2 },
                yaxis: 'y2'
              }
            ]}
            layout={{
              width: null,
              height: 400,
              autosize: true,
              paper_bgcolor: colors.background,
              plot_bgcolor: colors.background,
              font: { color: colors.text },
              xaxis: {
                title: inputsXAxisMode === 'distance' ? 'Distance (m)' : 'Time (mm:ss)',
                tickangle: -45,
                showgrid: true,
                gridcolor: colors.grid,
                zerolinecolor: colors.grid,
                tickfont: { color: colors.text }
              },
              yaxis: {
                title: '', // Hide label
                showticklabels: false, // Hide numbers
                range: [0, 100],
                showgrid: true,
                gridcolor: colors.grid,
                zerolinecolor: colors.grid,
                tickfont: { color: colors.text }
              },
              yaxis2: {
                title: '', // Hide label
                overlaying: 'y',
                side: 'right',
                showgrid: false,
                showticklabels: false, // Hide numbers
                tickfont: { color: colors.text }
              },
              title: {
                text: `Throttle, Brake & Speed - Lap ${formatLapTime(lapId)}`,
                font: { color: colors.text, size: 20 }
              },
              margin: { l: 50, r: 50, t: 50, b: 80 },
              legend: { font: { color: colors.text }, bgcolor: colors.background },
              showlegend: true
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        </div>
      );
    }
  }

  return (
    <div style={{ width: '100%', marginBottom: '2rem', backgroundColor: colors.background, padding: '1rem', borderRadius: '8px' }}>
      {/* Individual mode chart for a single lap */}
      <h3 style={{ color: colors.text, marginBottom: '1rem' }}>Inputs - Lap {formatLapTime(lapId)}</h3>
      <Plot
        data={[
          { 
            x: inputsXAxisMode === 'distance' ? dists : times, 
            y: throttle, 
            type: "scatter", 
            mode: "lines", 
            name: "Throttle", 
            line: { 
              color: colors.throttle,
              width: 2
            }
          },
          { 
            x: inputsXAxisMode === 'distance' ? dists : times, 
            y: brake, 
            type: "scatter", 
            mode: "lines", 
            name: "Brake", 
            line: { 
              color: colors.brake,
              width: 2
            }
          },
          { 
            x: inputsXAxisMode === 'distance' ? dists : times, 
            y: speed, 
            type: "scatter", 
            mode: "lines", 
            name: "Speed", 
            line: { 
              color: colors.speed,
              width: 2
            },
            yaxis: "y2"
          },
          { 
            x: inputsXAxisMode === 'distance' ? dists : times, 
            y: filteredLap.map(p => p.current_gear),
            type: 'scatter',
            mode: 'lines',
            name: 'Gear',
            line: { color: '#fff', width: 2, dash: 'dot' },
            visible: 'legendonly',
            yaxis: 'y3',
            showlegend: true,
            legendgroup: 'Gear',
            legendgrouptitle: { text: '' }
          }
        ]}
        layout={{
          width: null,
          height: 400,
          autosize: true,
          paper_bgcolor: colors.background,
          plot_bgcolor: colors.background,
          font: {
            color: colors.text
          },
          xaxis: { 
            title: inputsXAxisMode === 'distance' ? 'Distance (m)' : 'Time (mm:ss)',
            tickangle: -45,
            showgrid: true,
            gridcolor: colors.grid,
            zerolinecolor: colors.grid,
            tickfont: { color: colors.text },
            ticktext: tickText,
            tickvals: tickVals
          },
          yaxis: { 
            title: "Input (%)",
            showticklabels: true,
            range: [-5, 105],
            showgrid: true,
            gridcolor: colors.grid,
            zerolinecolor: colors.grid,
            tickfont: { color: colors.text }
          },
          yaxis2: {
            title: "Speed (km/h)",
            overlaying: 'y',
            side: 'right',
            showgrid: false,
            showticklabels: true,
            tickfont: { color: colors.text }
          },
          yaxis3: {
            title: '', // Hide label
            overlaying: 'y',
            side: 'right',
            position: 1,
            showgrid: false,
            showticklabels: false, // Hide numbers
            tickfont: { color: colors.text },
            tickmode: 'linear',
            dtick: 1,
            autorange: false,
            range: [0, 10]
          },
          title: {
            text: `Throttle, Brake & Speed - Lap ${formatLapTime(lapId)}`,
            font: {
              color: colors.text,
              size: 20
            }
          },
          margin: { l: 50, r: 50, t: 50, b: 80 },
          legend: {
            font: { color: colors.text },
            bgcolor: colors.background
          },
          showlegend: true
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
}

// --- PropTypes for type safety and documentation ---
InputsGraph.propTypes = {
  lap: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object
  ]),
  timeRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number
  }),
  lapId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array
  ]),
  merged: PropTypes.bool,
  mergedInputType: PropTypes.string,
  selectedLaps: PropTypes.array,
  inputsXAxisMode: PropTypes.string
};

export default InputsGraph;
